import { Database } from "#/types/supabase";
import { SupabaseClient } from "@supabase/supabase-js";
import { isBrowser } from "#/utils/environment";




export interface BrowserSupabase extends SupabaseClient<Database, "public"> {
  type: "browser"
}

export interface ServerSupabase extends SupabaseClient<Database, "public"> {
  type: "server"
}

export interface SuperAdminSupabase extends SupabaseClient<Database, "public"> {
  type: "super_admin"
}

export interface ImpersonationSupabase extends SupabaseClient<Database, "public"> {
  type: "impersonation"
}

type ClientType =
  | "BROWSER"
  | "SERVER"
  | "SUPER_USER"
  | "IMPERSONATION";

type BrowserClient<
  T extends ClientType
> = T extends "BROWSER" ? {
  browserSupabaseClient: BrowserSupabase
} : {
  browserSupabaseClient?: never
};

type ServerClient<
  T extends ClientType
> = T extends "SERVER" ? {
  serverAdminSupabaseClient: ServerSupabase
} : {
  serverAdminSupabaseClient?: never
};

type SuperAdminClient<
  T extends ClientType
> = T extends "SUPER_USER" ? {
  superUserSupabaseClient: SuperAdminSupabase
} : {
  superUserSupabaseClient?: never
};

type ImpersonationClient<
  T extends ClientType
> = T extends "IMPERSONATION" ? {
  serverImpersonationClient: ImpersonationSupabase
} : {
  serverImpersonationClient?: never
};

export type GetValidSupabaseClient<
  T extends ClientType = ClientType
> =
  & BrowserClient<T>
  & ServerClient<T>
  & SuperAdminClient<T>
  & ImpersonationClient<T>;

export const getValidSupabaseClient = (args: GetValidSupabaseClient): SupabaseClient<Database> => {
  if (!args) {
    throw new Error("No supabase client given to getValidSupabaseClient!");
  }

  const {
    browserSupabaseClient,
    superUserSupabaseClient,
    serverImpersonationClient,
    serverAdminSupabaseClient
  } = args;
  if (
    !browserSupabaseClient
    && !superUserSupabaseClient
    && !serverImpersonationClient
    && !serverAdminSupabaseClient
  ) {
    throw new Error("No supabase client given to getValidSupabaseClient!");
  }
  if (isBrowser()) {
    if (browserSupabaseClient) {
      return browserSupabaseClient;
    } else {
      throw new Error("to call getValidSupabaseClient from the browser, pass in a browser client.");
    }
  } else {
    if (serverImpersonationClient) {
      return serverImpersonationClient;
    } else if (superUserSupabaseClient) {
      return superUserSupabaseClient;
    } else if (serverAdminSupabaseClient) {
      return serverAdminSupabaseClient;
    } else {
      throw new Error("to call getValidSupabaseClient from the server, pass in a super_admin client.");
    }
  }
};
